import React, { useState, useEffect } from "react";
import {
    X,
    CheckCircle2,
    AlertCircle,
    Zap,
    Settings,
    HelpCircle,
    Sparkles,
    Cpu,
} from "lucide-react";

export interface IntegrationDocumentationModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialIntegrationId?: string | null;
    onOpenConfigure?: (integrationId: string) => void;
}

interface IntegrationDoc {
    id: string;
    name: string;
    category: "Notifications" | "Identity & SSO" | "ITSM & Helpdesk" | "Data & Ingestion";
    icon: string | React.ComponentType<{ className?: string }>;
    tagline: string;
    whatItDoes: string;
    whyUseIt: string[];
    howItWorks: {
        title: string;
        description: string;
    }[];
    stepByStep: {
        step: number;
        title: string;
        details: string;
        tip?: string;
    }[];
    configFields: {
        name: string;
        required: boolean;
        description: string;
        example: string;
    }[];
    testingAndTroubleshooting: {
        title: string;
        solution: string;
    }[];
}

const INTEGRATION_DOCS: IntegrationDoc[] = [
    {
        id: "webhook",
        name: "Webhook",
        category: "Notifications",
        icon: "/webhook.svg",
        tagline: "Stream real-time scan findings, vulnerability notifications, and platform events to any HTTP/HTTPS endpoint.",
        whatItDoes: "The Webhook integration enables the platform to instantly deliver security alerts, asset changes, and scan reports to your internal systems, SIEM/SOAR platforms, or automation tools (such as Zapier, Make, or custom REST servers).",
        whyUseIt: [
            "Real-time event streaming directly into your security operations center (SOC).",
            "Trigger automated remediation scripts and incident response playbooks.",
            "Connect with custom webhooks, SIEM log collectors, or messaging bots.",
            "Secure delivery with SHA-256 HMAC payload signatures and mutual authentication."
        ],
        howItWorks: [
            {
                title: "1. Event Triggered",
                description: "A scan completes, a new critical vulnerability is detected, or an automation policy triggers."
            },
            {
                title: "2. Payload Preparation & Token Exchange",
                description: "The platform formats the event into a standard JSON payload. If OAuth2 dynamic token exchange is configured, an access token is acquired and injected into the request header."
            },
            {
                title: "3. HMAC Signing & Ingestion",
                description: "If HMAC signing is enabled, the platform computes a SHA-256 hash using your secret and delivers the HTTP request (POST/PUT/PATCH) to your target URL."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Provide Endpoint URL & Method",
                details: "Enter your target HTTP or HTTPS webhook URL (e.g., https://api.yourdomain.com/webhooks) and select the HTTP method (POST, PUT, or PATCH).",
                tip: "Make sure the target server accepts incoming JSON requests and responds with an HTTP 200 OK."
            },
            {
                step: 2,
                title: "Configure Authentication",
                details: "Select your endpoint's auth type: None (Open), Bearer Token, API Key Header (e.g. X-API-Key), Basic Auth (username & password), or OAuth2 Dynamic Token Exchange.",
                tip: "For OAuth2, the backend will automatically request, cache, and inject the Bearer token without needing external token management."
            },
            {
                step: 3,
                title: "Add Custom Headers & HMAC (Optional)",
                details: "Configure any custom headers your server requires (like X-Tenant-ID). Optionally enable HMAC Payload Signing to cryptographically verify payload authenticity."
            },
            {
                step: 4,
                title: "Test Connection & Save",
                details: "Click 'Test Connection' to send a live ping to your server and preview the response status and latency in milliseconds. Once verified, click 'Save Changes'."
            }
        ],
        configFields: [
            { name: "Endpoint URL", required: true, description: "Destination webhook URL", example: "https://api.domain.com/webhooks" },
            { name: "HTTP Method", required: true, description: "POST, PUT, or PATCH", example: "POST" },
            { name: "Authentication Method", required: true, description: "None, Bearer, API Key, Basic, or OAuth2", example: "API Key Header" },
            { name: "HMAC Signing Secret", required: false, description: "Shared secret for SHA-256 signature verification", example: "whsec_abcd12345..." },
            { name: "Custom Headers", required: false, description: "Key-value pairs attached to outbound headers", example: "X-Tenant-ID: prod-01" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Test connection returned HTTP 401 or 403",
                solution: "Double check that your API Key, Bearer Token, or Basic credentials match the target server's authorization settings."
            },
            {
                title: "Connection Timed Out / HTTP 502",
                solution: "Ensure your destination server firewall allows incoming connections and that the URL starts with https://."
            },
            {
                title: "Self-signed or Internal SSL Certificate",
                solution: "If using an internal development server, enable 'Skip TLS Certificate Verification'."
            }
        ]
    },
    {
        id: "smtp",
        name: "Email (SMTP / Microsoft 365)",
        category: "Notifications",
        icon: "/email.png",
        tagline: "Deliver vulnerability notices, security alerts, and Custom Reports automation emails directly to mailboxes.",
        whatItDoes: "The Email integration routes all platform notifications, high-severity vulnerability alerts, user invites, and scheduled custom report exports through your enterprise email service.",
        whyUseIt: [
            "Send scheduled executive PDF & CSV custom reports automatically to client stakeholders.",
            "Instant email notifications whenever high or critical severity CVEs are discovered.",
            "Supports modern Microsoft Graph API (Office 365) without requiring insecure basic auth passwords.",
            "Integrate with transactional mail providers like SendGrid, Mailgun, and SMTP2Go."
        ],
        howItWorks: [
            {
                title: "1. Notification Event Generated",
                description: "A scheduled report generation runs or an asset vulnerability threshold is breached."
            },
            {
                title: "2. Dispatch Via Configured Provider",
                description: "The platform connects to your mail server (via Microsoft Graph REST API, SMTP TLS, or SendGrid API) using encrypted credentials."
            },
            {
                title: "3. Verified Inbox Delivery",
                description: "Alert emails and report attachments are delivered directly to designated recipient lists with your custom verified sender address."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Select Authentication Provider",
                details: "Choose between Microsoft Graph API (recommended for Microsoft 365 / Office 365), Basic SMTP, or API Key (SendGrid / Mailgun).",
                tip: "For Microsoft 365 organizations, Microsoft Graph is strongly recommended as basic SMTP authentication is deprecated by Microsoft."
            },
            {
                step: 2,
                title: "Fill In Sender Details & Credentials",
                details: "Provide your verified Sender Email Address (e.g. alerts@yourcompany.com). For Microsoft Graph, enter your Tenant ID, Client ID, and Client Secret. For SMTP, enter host, port, username, and password.",
                tip: "Make sure the App Registration in Azure AD has Mail.Send application permissions granted with Admin Consent."
            },
            {
                step: 3,
                title: "Enable Custom Reports Delivery",
                details: "Toggle 'Enable Email Delivery' to activate this mailbox for automated Custom Reports delivery and scheduled exports."
            },
            {
                step: 4,
                title: "Test Connection",
                details: "Click 'Test Connection' and enter a recipient address to send a live test email. Once confirmed, save configuration."
            }
        ],
        configFields: [
            { name: "Sender Email", required: true, description: "Outbound sender address", example: "alerts@yourcompany.com" },
            { name: "Tenant ID", required: true, description: "Azure AD Directory (Tenant) ID (for Microsoft 365)", example: "847291a0-1234-5678-abcd..." },
            { name: "Client ID", required: true, description: "Azure App Registration (Client) ID", example: "31f45678-abcd-ef01-2345..." },
            { name: "Client Secret", required: true, description: "Azure App Registration Client Secret Value", example: "••••••••" },
            { name: "custom_email", required: true, description: "Enables Custom Reports automation delivery", example: "true" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Microsoft Graph Error 403 Forbidden / Access Denied",
                solution: "In the Azure Portal (portal.azure.com), go to App Registrations > API Permissions > Add 'Mail.Send' (Application permission) and click 'Grant admin consent'."
            },
            {
                title: "SMTP Authentication Failed",
                solution: "Ensure SMTP AUTH is enabled for the mailbox in your Microsoft 365 Admin Center or verify your provider credentials."
            }
        ]
    },
    {
        id: "teams",
        name: "Microsoft Teams",
        category: "Notifications",
        icon: "/teams.svg",
        tagline: "Broadcast real-time scan findings, policy breaches, and alert cards directly into Microsoft Teams channels.",
        whatItDoes: "The Microsoft Teams integration sends instant interactive alert cards into designated Teams channels, ensuring your security and IT operations teams can quickly review vulnerabilities and take action without leaving Teams.",
        whyUseIt: [
            "Broadcast real-time notifications to team channels (e.g., #security-ops, #it-alerts).",
            "Rich interactive message cards highlighting CVE numbers, affected hostnames, and severity levels.",
            "Seamless OAuth2 installation using Microsoft 365 Admin Consent."
        ],
        howItWorks: [
            {
                title: "1. One-Click Admin Consent",
                description: "Authorize the platform app once via Microsoft 365 OAuth consent screen."
            },
            {
                title: "2. Channel Target Mapping",
                description: "Configure which Team and Channel receives critical and high severity scan alerts."
            },
            {
                title: "3. Real-Time Alert Publishing",
                description: "When alerts trigger, rich Adaptive Cards are posted directly to the selected Teams channel."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Initiate Installation",
                details: "Click 'Install' or 'Authorize' on the Microsoft Teams card in Integrations.",
                tip: "You must be signed in with an account that has Global Administrator or Privileged Role Administrator rights in your Microsoft 365 tenant."
            },
            {
                step: 2,
                title: "Grant Permissions",
                details: "Review the permissions requested by the app (Channel Message Posting) and click 'Accept' on the Microsoft consent dialog."
            },
            {
                step: 3,
                title: "Configure Channel Routing",
                details: "In integration settings, select the destination Team and Channel where alerts should be posted."
            }
        ],
        configFields: [
            { name: "Admin Consent", required: true, description: "Microsoft 365 tenant authorization", example: "Connected" },
            { name: "Channel Mapping", required: true, description: "Target Teams channel for alerts", example: "Security Operations" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Consent required by your organization",
                solution: "Have your organization's Microsoft 365 Global Administrator click 'Install' to grant tenant-wide consent for the bot app."
            }
        ]
    },
    {
        id: "slack",
        name: "Slack",
        category: "Notifications",
        icon: "/slack.svg",
        tagline: "Deliver instant security notifications, vulnerability alerts, and scan reports into Slack channels.",
        whatItDoes: "Connects your Slack workspace to receive automated notifications. High-risk vulnerability alerts, asset discoveries, and system messages are posted directly to designated channels as formatted Block Kit message cards.",
        whyUseIt: [
            "Keep engineers and security analysts updated without opening the dashboard.",
            "Color-coded alert cards (Red for Critical, Orange for High) with direct links to affected assets.",
            "Quick installation with standard Slack OAuth workspace authorization."
        ],
        howItWorks: [
            {
                title: "1. Workspace Authorization",
                description: "Authenticate with your Slack workspace and authorize the notification bot."
            },
            {
                title: "2. Channel Association",
                description: "Choose public or private Slack channels for receiving notifications."
            },
            {
                title: "3. Block Kit Card Dispatch",
                description: "Alerts are sent formatted with tags, severity levels, and deep links into scan details."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Click Install Slack",
                details: "Click 'Install' on the Slack card in Integrations to redirect to the Slack authorization page."
            },
            {
                step: 2,
                title: "Authorize Channel Access",
                details: "Select the channel (e.g. #security-alerts) and click 'Allow'."
            },
            {
                step: 3,
                title: "Send Test Alert",
                details: "Verify that the welcome message arrives in the selected Slack channel."
            }
        ],
        configFields: [
            { name: "Slack Workspace", required: true, description: "Connected Slack workspace", example: "Acme Corp" },
            { name: "Target Channel", required: true, description: "Default channel for alerts", example: "#security-alerts" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Bot cannot post to private channel",
                solution: "In Slack, open the private channel settings, click 'Integrations' > 'Add apps', and add our platform bot."
            }
        ]
    },
    {
        id: "microsoft-sso",
        name: "Microsoft SSO (Entra ID)",
        category: "Identity & SSO",
        icon: "/microsoftgraph.svg",
        tagline: "Enforce corporate Single Sign-On using Microsoft Entra ID (Azure AD) credentials.",
        whatItDoes: "Enables users and administrators to log in using their corporate Microsoft 365 / Azure AD credentials. Allows your IT department to enforce conditional access policies, Multi-Factor Authentication (MFA), and centralized identity management.",
        whyUseIt: [
            "Eliminate separate passwords and enforce corporate MFA policies.",
            "Instantly deprovision platform access when an employee leaves the company.",
            "Simple one-click login for all company staff."
        ],
        howItWorks: [
            {
                title: "1. Enterprise App Registration",
                description: "The platform integrates with your Entra ID tenant via OpenID Connect (OIDC)."
            },
            {
                title: "2. Identity Verification",
                description: "When a user logs in, Microsoft authenticates the user, checks MFA, and issues a secure signed token."
            },
            {
                title: "3. Role & Account Association",
                description: "The user is mapped to their platform account and permissions automatically."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Click Install Microsoft SSO",
                details: "Click 'Install' on the Microsoft SSO integration card."
            },
            {
                step: 2,
                title: "Authenticate with Azure AD Administrator",
                details: "Sign in with your Microsoft 365 / Azure AD admin credentials and grant consent for user profile reading."
            },
            {
                step: 3,
                title: "Verify Login Screen",
                details: "Navigate to the platform login page; users will now see the 'Sign in with Microsoft' button enabled."
            }
        ],
        configFields: [
            { name: "Tenant ID", required: true, description: "Your Microsoft Azure AD Tenant ID", example: "Connected automatically via OAuth" }
        ],
        testingAndTroubleshooting: [
            {
                title: "User not found after Microsoft login",
                solution: "Ensure the user's email address in Microsoft 365 matches their registered user account in the platform's Users & Roles settings."
            }
        ]
    },
    {
        id: "google-sso",
        name: "Google SSO",
        category: "Identity & SSO",
        icon: "/google.svg",
        tagline: "Log into the platform effortlessly with your corporate Google Workspace account.",
        whatItDoes: "Allows organization members to sign in using their Google Workspace accounts via Google OAuth 2.0 / OpenID Connect, eliminating credential duplication.",
        whyUseIt: [
            "Frictionless one-click authentication for teams using Google Workspace.",
            "Leverages Google's built-in 2-Step Verification and security controls.",
            "Automatic domain validation restricts login to authorized email domains."
        ],
        howItWorks: [
            {
                title: "1. OAuth Authorization",
                description: "Your Google Workspace admin connects the enterprise app to your organization."
            },
            {
                title: "2. Secure Google Login",
                description: "Users authenticate through Google's trusted sign-in dialog."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Install Google SSO",
                details: "Click 'Install' on the Google SSO integration card."
            },
            {
                step: 2,
                title: "Authorize with Google Admin Account",
                details: "Sign in with your Google Workspace admin account and accept permissions."
            },
            {
                step: 3,
                title: "Test User Sign In",
                details: "Sign out and use 'Sign in with Google' on the login screen."
            }
        ],
        configFields: [
            { name: "Google Workspace Domain", required: true, description: "Authorized email domain", example: "yourcompany.com" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Error 400: admin_policy_enforced",
                solution: "In Google Admin Console, go to Security > Access and data control > API controls and mark the platform app as 'Trusted'."
            }
        ]
    },
    {
        id: "google-workspace",
        name: "Google Workspace Directory",
        category: "Identity & SSO",
        icon: "/google.svg",
        tagline: "Synchronize user accounts, organizational units, and assets from Google Workspace Directory.",
        whatItDoes: "Connects with the Google Admin SDK Directory API to automatically synchronize employees, organizational units, and user groups into the platform.",
        whyUseIt: [
            "Automated user onboarding and offboarding synchronization.",
            "Asset discovery matching company email owners.",
            "Synchronize corporate departments and organizational structure."
        ],
        howItWorks: [
            {
                title: "1. Admin SDK Connection",
                description: "Connects to Google Workspace Directory API with Super Admin consent."
            },
            {
                title: "2. Periodic Sync",
                description: "The system reads active users, departments, and emails to update the platform user directory."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Click Install",
                details: "Click 'Install' on the Google Workspace card."
            },
            {
                step: 2,
                title: "Grant Directory API Read Permissions",
                details: "Sign in with a Google Super Admin account and grant read access to directory users."
            },
            {
                step: 3,
                title: "Trigger Initial Sync",
                details: "Click 'Sync Now' in Settings to import users and departments."
            }
        ],
        configFields: [
            { name: "Directory Scope", required: true, description: "Admin SDK User Read Scope", example: "Authorized" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Insufficient permissions to read directory",
                solution: "Make sure the authorizing account is assigned the Super Admin role in your Google Admin console."
            }
        ]
    },
    {
        id: "whatsapp",
        name: "WhatsApp Cloud API",
        category: "Notifications",
        icon: "/whatsapp.png",
        tagline: "Receive critical security alerts and urgent system incidents directly on WhatsApp.",
        whatItDoes: "Uses Meta's WhatsApp Cloud API to send high-priority alert messages to on-call administrators' mobile phones, ensuring urgent security incidents are never missed.",
        whyUseIt: [
            "Instant mobile delivery for urgent P1 security incidents and critical scan findings.",
            "High open rates compared to traditional email.",
            "Reliable global delivery powered by Meta Cloud infrastructure."
        ],
        howItWorks: [
            {
                title: "1. Meta Cloud API Connection",
                description: "Connects to Meta Graph API using your WhatsApp Business Phone Number ID and Access Token."
            },
            {
                title: "2. Template Message Delivery",
                description: "Sends pre-approved WhatsApp notification templates containing asset and vulnerability details."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Create Meta Business App",
                details: "In the Meta Developers Portal (developers.facebook.com), create a Business App with WhatsApp Cloud API enabled.",
                tip: "Generate a permanent System User token with 'whatsapp_business_messaging' permissions."
            },
            {
                step: 2,
                title: "Enter Phone Number ID & WABA ID",
                details: "Copy your Phone Number ID and WhatsApp Business Account ID from Meta and enter them in the platform modal."
            },
            {
                step: 3,
                title: "Configure Recipient Numbers",
                details: "Enter target mobile numbers with international country code (e.g., +1234567890)."
            },
            {
                step: 4,
                title: "Send Test Message",
                details: "Send a test WhatsApp alert to confirm your mobile phone receives the message."
            }
        ],
        configFields: [
            { name: "Phone Number ID", required: true, description: "Meta WhatsApp Cloud Phone Number ID", example: "1029384756..." },
            { name: "WABA ID", required: true, description: "WhatsApp Business Account ID", example: "9876543210..." },
            { name: "System User Token", required: true, description: "Permanent Meta Access Token", example: "EAAG..." },
            { name: "Recipient Numbers", required: true, description: "Comma-separated mobile numbers with country code", example: "+1234567890" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Meta Token Expired (#190)",
                solution: "Do not use temporary 24-hour test tokens. Create a permanent System User in Meta Business Manager and assign the token to WhatsApp."
            }
        ]
    },
    {
        id: "rundeskpro",
        name: "RunDeskPro (Helpdesk & ITSM)",
        category: "ITSM & Helpdesk",
        icon: "/rdp_logo.png",
        tagline: "Automatically convert scan findings into actionable support & IT tickets in RunDeskPro.",
        whatItDoes: "Integrates with RunDeskPro helpdesk software to automatically open, track, and update IT support tickets for newly discovered vulnerabilities, failed compliance checks, or asset issues.",
        whyUseIt: [
            "Bridge the gap between security vulnerability discovery and IT remediation.",
            "Automatically assign tickets to responsible IT departments and technicians.",
            "Keep ticket status synchronized with vulnerability fix verification."
        ],
        howItWorks: [
            {
                title: "1. Finding Detected",
                description: "An automated or manual scan identifies a vulnerability requiring remediation."
            },
            {
                title: "2. Ticket Creation via API",
                description: "The platform calls your RunDeskPro REST API and creates a ticket with detailed remediation steps and CVE references."
            },
            {
                title: "3. Resolution Sync",
                description: "When the vulnerability is verified resolved in subsequent scans, the ticket status can be updated."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Open RunDeskPro Settings",
                details: "Click 'Settings' or 'Install' on the RunDeskPro card."
            },
            {
                step: 2,
                title: "Enter API Credentials",
                details: "Provide your RunDeskPro Instance API URL (e.g., https://helpdesk.company.com), API Key, and API Secret."
            },
            {
                step: 3,
                title: "Set Target Domain",
                details: "Enter your corporate domain / tenant identifier."
            },
            {
                step: 4,
                title: "Save & Sync Metadata",
                details: "Save your settings and click 'Sync Metadata' to pull departments, priorities, and technicians."
            }
        ],
        configFields: [
            { name: "API URL", required: true, description: "Base URL of your RunDeskPro instance", example: "https://support.yourcompany.com" },
            { name: "API Key", required: true, description: "RunDeskPro REST API Key", example: "rdp_key_99..." },
            { name: "API Secret", required: true, description: "RunDeskPro API Secret Token", example: "••••••••" },
            { name: "Target Domain", required: true, description: "Helpdesk instance domain", example: "yourcompany.com" }
        ],
        testingAndTroubleshooting: [
            {
                title: "Failed to connect to RunDeskPro instance",
                solution: "Verify that the API URL does not have trailing slashes and that your firewall allows inbound HTTPS connections from the platform."
            }
        ]
    },
    {
        id: "external_api",
        name: "External API Integration",
        category: "Data & Ingestion",
        icon: Cpu,
        tagline: "Connect custom external APIs, data lakes, CMDBs, and SIEM platforms.",
        whatItDoes: "Provides a flexible external API connector for ingesting custom asset feeds, sending bidirectional scan telemetry, and triggering automated scans from third-party systems.",
        whyUseIt: [
            "Sync asset inventories from custom internal CMDB databases.",
            "Automate scanning pipelines from CI/CD systems.",
            "Flexible token and key-based authentication."
        ],
        howItWorks: [
            {
                title: "1. Credential Configuration",
                description: "Configure target API URL, authorization keys, and polling or pushing intervals."
            },
            {
                title: "2. Data Ingestion & Synchronization",
                description: "The platform communicates with your endpoint to synchronize assets and vulnerability data."
            }
        ],
        stepByStep: [
            {
                step: 1,
                title: "Click Configure / Install",
                details: "Open the External API settings modal."
            },
            {
                step: 2,
                title: "Enter API Credentials",
                details: "Specify the external endpoint URL and API access token."
            },
            {
                step: 3,
                title: "Verify Data Flow",
                details: "Save changes and verify data synchronization."
            }
        ],
        configFields: [
            { name: "Endpoint URL", required: true, description: "Target External API URL", example: "https://siem.company.com/api/v1" },
            { name: "API Key / Token", required: true, description: "Authentication Token", example: "ext_token_••••••••" }
        ],
        testingAndTroubleshooting: [
            {
                title: "401 Unauthorized Response",
                solution: "Ensure the API token has not expired and holds read/write permissions on the external endpoint."
            }
        ]
    }
];

export const IntegrationDocumentationModal: React.FC<IntegrationDocumentationModalProps> = ({
    isOpen,
    onClose,
    initialIntegrationId,
    onOpenConfigure
}) => {
    const [selectedId, setSelectedId] = useState<string>("webhook");
    const [activeTab, setActiveTab] = useState<"overview" | "setup" | "fields" | "troubleshoot">("overview");

    useEffect(() => {
        if (isOpen) {
            if (initialIntegrationId) {
                const normalized = initialIntegrationId.toLowerCase();
                const matched = INTEGRATION_DOCS.find(d =>
                    d.id === normalized ||
                    (normalized.includes("email") && d.id === "smtp") ||
                    (normalized.includes("deskpro") && d.id === "rundeskpro") ||
                    (normalized.includes("external") && d.id === "external_api") ||
                    (normalized.includes("whatsapp") && d.id === "whatsapp") ||
                    (normalized === "google-workspace" && d.id === "google-workspace") ||
                    (normalized === "google-sso" && d.id === "google-sso") ||
                    (normalized === "microsoft-sso" && d.id === "microsoft-sso") ||
                    (normalized === "teams" && d.id === "teams") ||
                    (normalized === "slack" && d.id === "slack") ||
                    (normalized === "webhook" && d.id === "webhook")
                );
                if (matched) {
                    setSelectedId(matched.id);
                }
            }
            setActiveTab("overview");
        }
    }, [isOpen, initialIntegrationId]);

    if (!isOpen) return null;

    const currentDoc = INTEGRATION_DOCS.find(d => d.id === selectedId) || INTEGRATION_DOCS[0];

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfigureClick = () => {
        onClose();
        if (onOpenConfigure) {
            onOpenConfigure(currentDoc.id);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
            onClick={handleBackdropClick}
        >
            <div className="bg-[#111827] border border-gray-700/60 rounded-2xl w-full max-w-4xl shadow-2xl animate-in zoom-in-95 duration-200 relative flex flex-col h-[85vh] max-h-[800px] overflow-hidden [color-scheme:dark]">
                {/* Header with Integration Identity & Action */}
                <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between shrink-0 bg-[#0F141F]">
                    <div className="flex items-center gap-3.5 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-[#0D1117] border border-gray-700/80 flex items-center justify-center p-2 shadow-md shrink-0">
                            {typeof currentDoc.icon === "string" ? (
                                <img src={currentDoc.icon} alt={currentDoc.name} className="w-full h-full object-contain" />
                            ) : (
                                <currentDoc.icon className="w-5 h-5 text-orange-400" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h2 className="text-base font-bold text-white leading-tight truncate">
                                    {currentDoc.name} Documentation
                                </h2>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-800 text-gray-300 border border-gray-700 shrink-0">
                                    {currentDoc.category}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xl">
                                {currentDoc.tagline}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white p-1.5 hover:bg-gray-800 rounded-lg transition-colors cursor-pointer shrink-0 ml-4"
                        title="Close documentation"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Inner Navigation Tabs */}
                <div className="px-6 py-2.5 border-b border-gray-800 bg-[#0D1117] flex items-center gap-1.5 text-xs shrink-0 overflow-x-auto">
                    <button
                        type="button"
                        onClick={() => setActiveTab("overview")}
                        className={`px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === "overview"
                            ? "bg-orange-500/15 text-orange-400 border border-orange-500/30"
                            : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                            }`}
                    >
                        Overview & Benefits
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("setup")}
                        className={`px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === "setup"
                            ? "bg-orange-500/15 text-orange-400 border border-orange-500/30"
                            : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                            }`}
                    >
                        Step-by-Step Guide
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("fields")}
                        className={`px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === "fields"
                            ? "bg-orange-500/15 text-orange-400 border border-orange-500/30"
                            : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                            }`}
                    >
                        Configuration Fields
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("troubleshoot")}
                        className={`px-3 py-1.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === "troubleshoot"
                            ? "bg-orange-500/15 text-orange-400 border border-orange-500/30"
                            : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                            }`}
                    >
                        Troubleshooting & FAQ
                    </button>
                </div>

                {/* Tab Content Body */}
                <div className="p-6 overflow-y-auto flex-1 custom-scroll space-y-6 bg-[#111827]">
                    {/* TAB 1: OVERVIEW */}
                    {activeTab === "overview" && (
                        <div className="space-y-5 animate-in fade-in duration-150">
                            {/* What it does */}
                            <div className="p-4 bg-[#0D1117] border border-gray-800 rounded-xl space-y-2">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-200">
                                    <Sparkles className="w-4 h-4 text-orange-400" />
                                    <span>What is {currentDoc.name}?</span>
                                </div>
                                <p className="text-xs text-gray-300 leading-relaxed">
                                    {currentDoc.whatItDoes}
                                </p>
                            </div>

                            {/* Key Benefits */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                                    Why Connect {currentDoc.name}?
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    {currentDoc.whyUseIt.map((benefit, idx) => (
                                        <div key={idx} className="p-3 bg-gray-900/50 border border-gray-800 rounded-xl flex items-start gap-2.5 text-xs text-gray-300">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                            <span>{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* How it works architecture */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                                    How It Works & Data Flow
                                </h4>
                                <div className="space-y-2">
                                    {currentDoc.howItWorks.map((step, idx) => (
                                        <div key={idx} className="p-3.5 bg-[#0D1117] border border-gray-800 rounded-xl flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold flex items-center justify-center shrink-0">
                                                {idx + 1}
                                            </div>
                                            <div>
                                                <h5 className="text-xs font-bold text-gray-200">{step.title}</h5>
                                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{step.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 2: STEP-BY-STEP */}
                    {activeTab === "setup" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-300 text-xs flex items-center gap-2">
                                <HelpCircle className="w-4 h-4 shrink-0 text-blue-400" />
                                <span>Follow this walkthrough to configure and verify {currentDoc.name} in less than 5 minutes.</span>
                            </div>

                            <div className="space-y-3">
                                {currentDoc.stepByStep.map((s) => (
                                    <div key={s.step} className="p-4 bg-[#0D1117] border border-gray-800 rounded-xl space-y-2">
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-6 h-6 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 text-xs font-bold flex items-center justify-center shrink-0">
                                                {s.step}
                                            </span>
                                            <h5 className="text-xs font-bold text-white">{s.title}</h5>
                                        </div>
                                        <p className="text-xs text-gray-300 pl-8 leading-relaxed">
                                            {s.details}
                                        </p>
                                        {s.tip && (
                                            <div className="ml-8 p-2.5 bg-gray-900/60 border border-gray-800 rounded-lg text-[11px] text-orange-300 flex items-start gap-2">
                                                <Zap className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
                                                <span><strong className="text-orange-400">Pro Tip:</strong> {s.tip}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TAB 3: CONFIGURATION FIELDS */}
                    {activeTab === "fields" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            <div className="overflow-x-auto border border-gray-800 rounded-xl bg-[#0D1117]">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-gray-800 bg-gray-900/60 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                            <th className="py-2.5 px-4">Field Name</th>
                                            <th className="py-2.5 px-3">Requirement</th>
                                            <th className="py-2.5 px-4">Description</th>
                                            <th className="py-2.5 px-4">Example Value</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/60">
                                        {currentDoc.configFields.map((field, idx) => (
                                            <tr key={idx} className="hover:bg-gray-900/40 transition-colors">
                                                <td className="py-3 px-4 font-mono font-bold text-orange-400">
                                                    {field.name}
                                                </td>
                                                <td className="py-3 px-3">
                                                    {field.required ? (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/10 border border-rose-500/20 text-rose-400">
                                                            Required
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-800 text-gray-400">
                                                            Optional
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-gray-300">
                                                    {field.description}
                                                </td>
                                                <td className="py-3 px-4 font-mono text-gray-400 text-[11px]">
                                                    {field.example}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: TROUBLESHOOTING */}
                    {activeTab === "troubleshoot" && (
                        <div className="space-y-3 animate-in fade-in duration-150">
                            {currentDoc.testingAndTroubleshooting.map((item, idx) => (
                                <div key={idx} className="p-4 bg-[#0D1117] border border-gray-800 rounded-xl space-y-2">
                                    <div className="flex items-center gap-2 text-xs font-bold text-rose-400">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        <span>{item.title}</span>
                                    </div>
                                    <p className="text-xs text-gray-300 pl-6 leading-relaxed">
                                        {item.solution}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3.5 border-t border-gray-800 bg-[#0F141F] flex items-center justify-between shrink-0">
                    <div className="text-[11px] text-gray-500 hidden sm:block">
                        Need custom help? Contact your enterprise platform administrator.
                    </div>

                    <div className="flex items-center gap-3 ml-auto">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-700/80 rounded-xl text-xs font-semibold text-gray-300 hover:bg-gray-800 transition-all cursor-pointer"
                        >
                            Close
                        </button>

                        <button
                            type="button"
                            onClick={handleConfigureClick}
                            className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-950/30 transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                            <Settings className="w-3.5 h-3.5" />
                            <span>Configure {currentDoc.name}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationDocumentationModal;
